import React, { useCallback, useContext, useEffect, useState } from 'react';
import PageActionContext from '@stores/page-action-context';
import AppDataContext from '@stores/app-data-context';
import useForm, { buildInput } from '@ui/forms/use-form';
import FormHtmlInput from '@ui/forms/form-input';
import OptionsBox from '@components/ui/options-box';

const FormLoanPreferences = ({ data }) => {
    const {
        buildYear,
        typeOfVehicle,
        vehiclePrice,
        deposit,
        baloonPayment,
        loanTerm,
        stockId,
        minLoanAmount,
        maxLoanAmount,
        loanAmountRangeErrorMessage
    } = data;
    const [loanAmount, setLoanAmount] = useState(null);
    const [loanAmountIsValid, setLoanAmountIsValid] = useState(true);
    const pageActionCxt = useContext(PageActionContext);
    const appDataCxt = useContext(AppDataContext);
    const cxtData = appDataCxt.getLoanRequirements() ?? {};
    const [assetCondition, setAssetCondition] = useState(
        cxtData?.assetCondition
    );

    const validateDepositAmount = ({ value, others }) => {
        const deposit = parseFloat(value, 10);
        const vehiclePrice = parseFloat(others.vehiclePrice, 10);
        validateLoanAmount(vehiclePrice - deposit);
        setLoanAmount(vehiclePrice - deposit);
        return vehiclePrice - minLoanAmount >= deposit;
    };

    const validateVehiclePrice = ({ value, others }) => {
        const deposit = parseFloat(others.deposit, 10);
        const loanValue = parseFloat(value, 10);

        if (deposit > 0) {
            validateLoanAmount(loanValue - deposit);
            setLoanAmount(loanValue - deposit);
            return deposit <= loanValue - minLoanAmount;
        } else {
            validateLoanAmount(parseFloat(others.vehiclePrice, 10));
            setLoanAmount(parseFloat(others.vehiclePrice, 10));
            return true;
        }
    };

    const validateLoanAmount = (value) => {
        setLoanAmountIsValid((value >= minLoanAmount && value <= maxLoanAmount))
        return loanAmountIsValid;
    };

    const handleVehicleTypeOptionSelected = (opt) => {
        if (opt === null) return;
        setAssetCondition(opt.value);
    };

    const showAssetConditionOptions = () => {
        if (form.values.buildYear >= new Date().getFullYear() - 3) {
            return true;
        }
        return false;
    };

    const isDirtyAssetCondition=()=>{
        return (showAssetConditionOptions() && assetCondition !== cxtData?.assetCondition );
    }

    const getAssetCondition = () => {
        if (showAssetConditionOptions()) return assetCondition;
        return 'used';
    };

    const inputs = [
        buildInput(buildYear, cxtData?.buildYear),
        buildInput(vehiclePrice, cxtData.vehiclePrice, [
            {
                name: 'vehicle-minimum-amount-error',
                message: data.vehicleMinimumAmountError,
                check: useCallback((value) => validateVehiclePrice(value)),
            },
        ]),
        buildInput(deposit, cxtData.deposit, [
            {
                name: 'loan-deposit-error',
                message: data.loanDepositError,
                check: useCallback((value) => validateDepositAmount(value)),
            },
        ]),
        buildInput(baloonPayment, cxtData.baloonPayment),
        buildInput(loanTerm, cxtData.loanTerm),
    ];

    const form = useForm({
        inputs: inputs,
    });

    useEffect(() => {
        pageActionCxt.reset();
    }, []);

    useEffect(() => {
        pageActionCxt.setNextDisabled(!form.isValid || !loanAmountIsValid);
    }, [form.isValid, loanAmountIsValid]);

    const handleSubmit = (e) => {
        e?.preventDefault();
        if (pageActionCxt.isNextDisabled) return;

        if(form.dirty || isDirtyAssetCondition()){
            appDataCxt.setLoanRequirements({
                buildYear: parseInt(form.values.buildYear, 10) ?? 0,
                assetCondition: getAssetCondition(),
                vehiclePrice: parseInt(form.values.vehiclePrice, 10) ?? 0,
                loanAmount: parseInt(loanAmount, 10) ?? 0,
                deposit: parseInt(form.values.deposit, 10) ?? 0,
                baloonPayment: parseInt(form.values.baloonPayment, 10) ?? 0,
                loanTerm: parseInt(form.values.loanTerm, 10) ?? 0,
                stockId: stockId
            });
        }

        const nextEventData = pageActionCxt.nextEventData;

        if (nextEventData) {
            pageActionCxt.navigate(nextEventData.url, nextEventData.isExternal);
        }
    };

    useEffect(() => {
        if (pageActionCxt.isNextClicked) {
            handleSubmit(null);
            pageActionCxt.setNextClicked(false);
        }
    }, [pageActionCxt.isNextClicked]);

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <FormHtmlInput data={buildYear} form={form} />
            {showAssetConditionOptions() && (
                <OptionsBox
                    data={{
                        ...typeOfVehicle,
                        options: typeOfVehicle.options.map((o) => {
                            return {
                                ...o,
                                isSelected: o.value === assetCondition,
                            };
                        }),
                    }}
                    onOptionSelected={handleVehicleTypeOptionSelected}
                />
            )}
            <FormHtmlInput data={vehiclePrice} form={form} />
            <FormHtmlInput data={deposit} form={form} />
            <div className="h-24">
                <div className="flex gap-4 h-20 bg-skin-primary-light rounded-md border border-skin-primary w-full px-4 py-4">                
                    <div className="w-full text-center">
                        <p>{data.loanAmount.label}</p>
                        <p className="text-2xl text-skin-primary font-semibold">{`${data.loanAmount.prefixSign} ${loanAmount > 0 ? loanAmount.toLocaleString() : 0}`}</p>
                    </div>                
                </div>
                {!loanAmountIsValid && <div class="text-red-600 text-sm">{loanAmountRangeErrorMessage}</div>}
            </div>            
            <FormHtmlInput data={baloonPayment} form={form} />
            <FormHtmlInput data={loanTerm} form={form} />
            <button type="submit" className="hidden">
                Submit
            </button>
        </form>
    );
};

export default FormLoanPreferences;
